import type { EmailPayload } from "~/types"

export const useEmail = () => {
  const { setLoading } = useUIStore()
  const { businessEmail, engApiToken } = useEnvVariables()
  const toast = useSharedToast()

  const sendNewsletterSubscriptionEmail = async (
    email: string,
    privacies: {
      privacy1: boolean,
      privacy2: boolean,
      privacy3: boolean,
      privacy4: boolean,
    }
  ): Promise<void> => {
    try {
      setLoading(true)

      const emailPayload: EmailPayload = {
        subject: '[SOTTOSCRIZIONE NEWSLETTER]',
        body: `
          <p>Ricevuta richiesta di iscrizione da: <strong>${email}</strong>.</p>
          <br />
          <p>Le scelte privacy dell'utente sono:</p>
          <ul>
            <li>conferma lettura informativa privacy: <strong>${privacies.privacy1}</strong>;</li>
            <li>trattamento dati personali finalita' di marketing: <strong>${privacies.privacy2}</strong>;</li>
            <li>trattamento dati personali finalita' di cessione a terzi: <strong>${privacies.privacy3}</strong>;</li>
            <li>trattamento dati personali finalita' di profilazione: <strong>${privacies.privacy4}</strong>.</li>
          </ul>
        `,
        to: [businessEmail],
      }

      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${engApiToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailPayload)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      toast.add({
        severity: 'success',
        summary: 'Iscrizione alla newsletter',
        detail: 'La tua iscrizione alla neswletter è avvenuta con successo',
        group: 'bc',
        life: 10_000,
      })
    } catch (error) {
      toast.add({
        severity: 'warn',
        summary: 'Qualcosa è andato storto',
        detail: 'Per favore riprovare a breve dopo aver ricaricato la pagina.',
        group: 'bc',
        life: 10_000,
      })
      throw new Error('An error occurred while subscribing to the newsletter')
    } finally {
      setLoading(false)
    }
  }

  const sendContactUsEmail = async (
    email: string,
    phone: string,
    message: string,
    privacies: {
      privacy1: boolean,
      privacy2: boolean,
      privacy3: boolean,
      privacy4: boolean,
    },
    file: File | null,
    context: 'contact' | 'career' = 'career'
  ): Promise<void> => {
    try {
      setLoading(true)

      let attachmentBase64: string | null = null
      let attachmentName: string | null = null
      let attachmentType: string | null = null

      if (file) {
        const buffer = await file.arrayBuffer()
        const bytes = new Uint8Array(buffer)
        let binary = ''
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i])
        }
        attachmentBase64 = btoa(binary)
        attachmentName = file.name
        attachmentType = file.type
      }

      // Costruiamo l'oggetto completo direttamente per la chiamata fetch
      const requestBody = {
        subject: context === 'contact' ? 'Nuovo Contatto da RisparmiaTu.com' : 'Nuova Candidatura da RisparmiaTu.com',
        body: `
          <p><strong>Email:</strong> ${email}</p>
          ${phone ? `<p><strong>Telefono:</strong> +39 ${phone}</p>` : ''}
          <br />
          <p><strong>Messaggio:</strong></p>
          <p>${message}</p>
          <br />
          <hr />
          <p><small>Fonte: risparmiatu.com — Sezione ${context === 'contact' ? 'Contatti' : 'Lavora Con Noi'}</small></p>
        `,
        to: context === 'contact' ? ['marketing@eutel2005.com'] : ['lavoraconnoi@eutel2005.com'],
        emailRaw: email,
        context: context,
        ...(attachmentBase64 && {
          attachment: {
            base64: attachmentBase64,
            name: attachmentName,
            type: attachmentType,
          }
        })
      }

      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${engApiToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody) // ← Modifica applicata qui
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      toast.add({
        severity: 'success',
        summary: 'Invio messaggio',
        detail: 'Il tuo messaggio è stato inviato con successo, un collaboratore ti risponderà al più presto.',
        group: 'bc',
        life: 10_000,
      })
    } catch (error) {
      toast.add({
        severity: 'warn',
        summary: 'Qualcosa è andato storto',
        detail: 'Per favore riprovare a breve dopo aver ricaricato la pagina.',
        group: 'bc',
        life: 10_000,
      })
      console.error(error)
      throw new Error('An error occurred while sending the email')
    } finally {
      setLoading(false)
    }
  }

  return {
    sendNewsletterSubscriptionEmail,
    sendContactUsEmail
  }
}
